
import { useState, useEffect, useRef, useCallback } from 'react';

interface UseRecordingIntervalModalProps {
  isRecording: boolean;
  onForceFinish: () => void;
  intervalMinutes?: number;
}

export const useRecordingIntervalModal = ({
  isRecording,
  onForceFinish,
  intervalMinutes = 15
}: UseRecordingIntervalModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);

  const clearExistingInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleContinue = useCallback(() => {
    setShowModal(false);
    // Restart the interval for the next 15 minutes
    clearExistingInterval();
    intervalRef.current = setInterval(() => {
      setShowModal(true);
    }, intervalMinutes * 60 * 1000);
  }, [intervalMinutes, clearExistingInterval]);

  const handleFinalize = useCallback(() => {
    setShowModal(false);
    clearExistingInterval();
    recordingStartTimeRef.current = null;
    onForceFinish();
  }, [onForceFinish, clearExistingInterval]);

  useEffect(() => {
    if (isRecording && !recordingStartTimeRef.current) {
      // Start recording - set up the 15-minute interval
      recordingStartTimeRef.current = Date.now();
      clearExistingInterval();
      
      intervalRef.current = setInterval(() => {
        setShowModal(true);
      }, intervalMinutes * 60 * 1000);
      
      console.log(`Recording started - modal will appear every ${intervalMinutes} minutes`);
    } else if (!isRecording && recordingStartTimeRef.current) {
      // Stop recording - clear everything
      clearExistingInterval();
      recordingStartTimeRef.current = null;
      setShowModal(false);
      console.log('Recording stopped - interval cleared');
    }
  }, [isRecording, intervalMinutes, clearExistingInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearExistingInterval();
    };
  }, [clearExistingInterval]);

  return {
    showModal,
    handleContinue,
    handleFinalize
  };
};
